import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp } from "lucide-react";

const CARDS = [
  {
    type: "img",
    img: "https://metaadsguys.com.au/proof-1.webp",
    label: "House Washing Campaign",
    stats: [{ value: "61", k: "Enquiries" }, { value: "$13.63", k: "Cost Per Enquiry" }, { value: "$832.43", k: "Ad Spend" }],
    title: "61 House Wash Enquiries @ $13.63 CPE",
    desc: "Approx. 18 quotes issued, ~$14,200 in booked jobs. Suburban service area, broad local targeting.",
  },
  {
    type: "img",
    img: "https://metaadsguys.com.au/proof-2.webp",
    label: "Roof Washing Campaign",
    stats: [{ value: "23", k: "Enquiries" }, { value: "$17.91", k: "Cost Per Enquiry" }, { value: "$411.86", k: "Ad Spend" }],
    title: "23 Roof Wash Enquiries In 10 Days",
    desc: "Approx. 11 quotes issued, ~$18,600 in booked jobs. Offer-led creative aimed at higher-ticket roof work.",
  },
  {
    type: "chat",
    label: "Pressure Cleaning Campaign",
    title: "Pressure-Cleaning Quote Requests",
    desc: "Enquiries pre-qualified by service, property size and timeframe — quotes went out at the right price first time.",
  },
  {
    type: "chart",
    label: "House + Pressure Combo Campaign",
    stats: [{ value: "34", k: "Enquiries" }, { value: "$14.12", k: "Cost Per Enquiry" }, { value: "$480.03", k: "Ad Spend" }],
    title: "34 Combined Enquiries @ $14.12 CPE",
    desc: "Approx. 12 quotes issued, ~$9,800 in booked jobs. Ongoing creative rotation kept costs stable at volume.",
  },
];

const StatRow = ({ stats }) => (
  <div className="grid grid-cols-3 gap-2 px-1">
    {stats.map((s) => (
      <div key={s.k} className="flex flex-col">
        <span className="text-base font-black text-brand-black sm:text-lg">{s.value}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-brand-grayMid">{s.k}</span>
      </div>
    ))}
  </div>
);

const LineChart = () => {
  const TRUCK_PATH =
    "M0,150 L40,140 L80,135 L120,110 L160,95 L200,75 L240,80 L280,55 L320,40";

  return (
    <div className="aspect-[4/3] w-full overflow-hidden rounded-lg bg-gradient-to-b from-blue-50/40 to-white p-3 ring-1 ring-gray-100">
      <svg viewBox="0 0 320 180" className="h-full w-full" aria-hidden="true">
        <defs>
          <linearGradient id="caseFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#1A56FF" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#1A56FF" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Filled area */}
        <path d={`${TRUCK_PATH} L320,180 L0,180 Z`} fill="url(#caseFill)" />

        {/* Animated trend line — draws itself in, loops */}
        <path
          d={TRUCK_PATH}
          fill="none"
          stroke="#1A56FF"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="900"
          strokeDashoffset="900"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="900"
            to="0"
            dur="3.5s"
            begin="0s;line.end+1.5s"
            fill="freeze"
            id="line"
          />
        </path>

        {/* Endpoint glow */}
        <circle cx="320" cy="40" r="14" fill="#1A56FF" opacity="0.1" />
        <circle cx="320" cy="40" r="3.5" fill="#1A56FF" />
      </svg>
    </div>
  );
};

const SMS_SCRIPT = [
  { from: "cust", text: "Hi, after a quote for pressure cleaning our driveway please." },
  { from: "op", text: "Hey! Happy to help. Roughly how large is the driveway — single or double car?" },
  { from: "cust", text: "Double, plus a footpath area." },
  { from: "op", text: "Perfect. Concrete, pavers, or exposed aggregate?" },
  { from: "cust", text: "Exposed aggregate." },
  { from: "op", text: "Great. What's your street address?" },
  { from: "cust", text: "42 Campbell Pde, Bondi NSW." },
  { from: "op", text: "Locked in — quote on its way 🙌" },
];

const TYPING_MS = 700;
const READ_MS = 1300;

const ChatBubble = () => {
  const [visible, setVisible] = useState([]); // array of indices currently shown
  const [typing, setTyping] = useState(null); // 'op' | 'cust' | null
  const scrollerRef = useRef(null);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    const queue = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      timeoutsRef.current.push(id);
    };

    const run = () => {
      setVisible([]);
      setTyping(null);
      let cursor = 400;
      SMS_SCRIPT.forEach((msg, i) => {
        queue(() => setTyping(msg.from), cursor);
        cursor += TYPING_MS;
        queue(() => {
          setTyping(null);
          setVisible((v) => [...v, i]);
        }, cursor);
        cursor += READ_MS;
      });
      // Loop
      queue(() => {
        if (!cancelled) run();
      }, cursor + 1800);
    };

    run();
    return () => {
      cancelled = true;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // Auto-scroll to bottom whenever a new bubble appears or typing dots show
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [visible, typing]);

  return (
    <div className="aspect-[4/3] w-full overflow-hidden rounded-lg bg-gradient-to-b from-blue-50/40 to-white ring-1 ring-gray-100">
      {/* Phone-style header */}
      <div className="flex items-center justify-between border-b border-gray-200/70 bg-white/70 px-3 py-1.5 text-[10px] font-bold text-brand-grayMid backdrop-blur">
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          SMS · Solar Cleaning Lead
        </span>
        <span>Bondi, NSW</span>
      </div>

      {/* Scrollable conversation */}
      <div
        ref={scrollerRef}
        className="hide-scrollbar flex h-[calc(100%-26px)] flex-col gap-2 overflow-y-auto px-3 py-3"
      >
        <AnimatePresence initial={false}>
          {visible.map((i) => {
            const msg = SMS_SCRIPT[i];
            const isOp = msg.from === "op";
            return (
              <motion.div
                key={`m-${i}`}
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-[11px] leading-relaxed shadow-sm sm:text-xs ${
                  isOp
                    ? "self-end rounded-br-sm bg-brand-blue text-white"
                    : "self-start rounded-bl-sm bg-gray-100 text-brand-black"
                }`}
              >
                {msg.text}
              </motion.div>
            );
          })}
          {typing && (
            <motion.div
              key={`typing-${typing}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex items-center gap-1 rounded-2xl px-3 py-2 ${
                typing === "op"
                  ? "self-end rounded-br-sm bg-brand-blue/90"
                  : "self-start rounded-bl-sm bg-gray-100"
              }`}
            >
              {[0, 1, 2].map((d) => (
                <motion.span
                  key={d}
                  className={`block h-1.5 w-1.5 rounded-full ${
                    typing === "op" ? "bg-white/90" : "bg-brand-grayMid"
                  }`}
                  animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
                  transition={{
                    duration: 0.9,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: d * 0.15,
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const SENJA_SRC =
  "https://widget.senja.io/widget/522d6afc-3de8-4686-b080-c1cad3cbe934/platform.js";

const SenjaWidget = () => {
  useEffect(() => {
    if (document.querySelector(`script[src="${SENJA_SRC}"]`)) return;
    const s = document.createElement("script");
    s.src = SENJA_SRC;
    s.async = true;
    s.type = "text/javascript";
    document.body.appendChild(s);
  }, []);

  return (
    <div
      className="senja-embed"
      data-id="522d6afc-3de8-4686-b080-c1cad3cbe934"
      data-mode="shadow"
      data-lazyload="false"
      style={{ display: "block", width: "100%" }}
    />
  );
};

const Results = () => {
  return (
    <section id="results" className="mx-auto max-w-6xl px-5 py-14 sm:px-8 sm:py-20 scroll-mt-20">
      <div className="text-center">
        <h2 className="text-3xl font-black uppercase tracking-tight text-brand-black sm:text-4xl">
          Real <span className="text-brand-blue">Exterior Cleaning</span> Results
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-brand-grayMid sm:text-base">
          See what our campaigns have generated for house washing, roof washing and pressure-cleaning businesses across Australia.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
        {CARDS.map((c) => (
          <article
            key={c.label}
            className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md"
          >
            <div className="p-4">
              <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-brand-grayMid">{c.label}</p>
              {c.stats && <div className="mb-3"><StatRow stats={c.stats} /></div>}
              {c.type === "img" && (
                <img src={c.img} alt="Campaign performance screenshot" className="aspect-[4/3] w-full rounded-lg object-cover ring-1 ring-gray-100" loading="lazy" />
              )}
              {c.type === "chat" && <ChatBubble />}
              {c.type === "chart" && <LineChart />}
            </div>
            <div className="flex flex-col gap-1 border-t border-gray-100 p-4 text-center">
              <h3 className="text-sm font-black uppercase tracking-wide text-brand-black flex items-center justify-center gap-1.5">
                {c.type === "chart" && <TrendingUp className="h-4 w-4 text-brand-blue" />}
                {c.title}
              </h3>
              <p className="text-xs leading-relaxed text-brand-grayMid">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
      <p className="mt-6 text-center text-xs italic text-brand-grayMid">
        These are real client results. Individual results will vary depending on territory, offer, budget, availability and sales performance.
      </p>

      {/* Senja testimonials widget */}
      <div className="mt-14 sm:mt-20">
        <div className="mb-8 text-center">
          <h3 className="text-2xl font-black uppercase tracking-tight text-brand-black sm:text-3xl">
            What Our <span className="text-brand-blue">Clients</span> Say
          </h3>
          <p className="mt-3 text-sm text-brand-grayMid">Real testimonials from local businesses we&apos;ve worked with.</p>
        </div>
        <SenjaWidget />
      </div>
    </section>
  );
};

export default Results;
